// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDashboardIsHomepage from "@insite/client-framework/Store/Context/Handlers/SetDashboardIsHomepage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    dashboardIsHomepage: state.context.session.dashboardIsHomepage ?? undefined,
    // XNG-Change
    userName: state.context.session.userName,
});

const mapDispatchToProps = {
    setDashboardIsHomepage,
};

export interface MyAccountHomepageSelectorStyles {
    checkboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    checkbox?: CheckboxPresentationProps;
    // XNG-Change
    container?: InjectableCss;
    welcomeMessage?: TypographyPresentationProps;
}

export const myAccountHomepageSelectorStyles: MyAccountHomepageSelectorStyles = {
    checkboxGroup: {
        css: css`
            align-items: flex-end;
            height: 100%;
            justify-content: center;
        `,
    },
};

const styles = myAccountHomepageSelectorStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const MyAccountHomepageSelector = ({ dashboardIsHomepage, setDashboardIsHomepage, fields, userName }: Props) => {
    const [isCheckboxSelected, setIsCheckboxSelected] = useState(dashboardIsHomepage);
    const dashboardIsHomepageChangeHandler = (event: React.SyntheticEvent<Element, Event>, value: boolean) => {
        setIsCheckboxSelected(value);
        setDashboardIsHomepage({ dashboardIsHomepage: value });
    };
    // XNG-Changes: Add container, Show title
    return (
        <StyledWrapper {...styles.container}>
            {fields.showWelcomeMessage && (
                <Typography {...styles.welcomeMessage}>
                    {translate("Welcome, ")} {userName}
                </Typography>
            )}
            <CheckboxGroup {...styles.checkboxGroup}>
                <Checkbox
                    {...styles.checkbox}
                    onChange={dashboardIsHomepageChangeHandler}
                    checked={isCheckboxSelected}
                    data-test-selector="makeMyHomePageCheckbox"
                >
                    {siteMessage("Dashboard_MakeMyHomepage")}
                </Checkbox>
            </CheckboxGroup>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyAccountHomepageSelector),
    definition: {
        allowedContexts: ["MyAccountPage"],
        group: "My Account",
        displayName: "Homepage Selector",
        // XNG-Change
        fieldDefinitions: [
            {
                name: "showWelcomeMessage",
                displayName: "Show welcome message",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
        ],
    },
};

export default widgetModule;
